import { useCallback, useState } from 'react';

import {
  AssociationWOData,
  CheckWorkOrderRegistryParams,
} from '../../../api/registryAssociation/types';
import { AssociateWorkOrderDialog } from '../../../components/AssociateWorkOrderDialog';
import { WhiteCircularProgress } from '../../../components/UI/Typography';
import { Constants } from '../../../constants/entrance';
import { isPlaceholderSelectOption } from '../../../helpers';
import { useAssociateWorkOrder } from '../../../hooks/useAssociateWorkOrder';
import useErrorMessage from '../../../hooks/useErrorMessage';
import { SubmitBox, SubmitButton } from '../styles';

interface NewPropertyButtonsProps {
  submitLoading: boolean;
  registerNumber: string;
  book: string;
  registryOffice: string;
  shouldAssociate: boolean | null;
  handleAssociate: (value: boolean | null) => void;
  setAssociateIds: (values: number[]) => void;
}

export default function NewPropertyButtons({
  submitLoading,
  registerNumber,
  book,
  registryOffice,
  shouldAssociate,
  handleAssociate,
  setAssociateIds,
}: NewPropertyButtonsProps): JSX.Element {
  const [openAssociateDialog, setOpenAssociateDialog] = useState(false);
  const [workOrders, setWorkOrders] = useState<AssociationWOData[]>([]);

  const { getMatchRegistryWorkOders, loading: loadingAssociateData } =
    useAssociateWorkOrder();
  const { logDevError } = useErrorMessage();

  const onCloseDialog = (): void => setOpenAssociateDialog(false);

  const submitForm = (): Promise<void> => {
    return new Promise((resolve) => {
      const form = document.getElementById('entrance');
      if (form instanceof HTMLFormElement) {
        form.requestSubmit();
        setTimeout(resolve, 100);
      } else {
        resolve();
      }
    });
  };

  const handleButtonFunction = useCallback(async () => {
    handleAssociate(null);
    try {
      const field = [registerNumber, book, registryOffice];
      if (field.some(isPlaceholderSelectOption)) {
        await submitForm();
      } else {
        const params: CheckWorkOrderRegistryParams = {
          book,
          registration_number: registerNumber,
          registry_office: registryOffice,
        };

        const result = await getMatchRegistryWorkOders(params);

        if (!result || result.length === 0) {
          await submitForm();
        } else {
          const associateIds = result.map((os) => os.id);
          setAssociateIds(associateIds);
          setWorkOrders(result);
          setOpenAssociateDialog(true);
        }
      }
    } catch (error) {
      logDevError(`Error on loading match work orders to associate: ${error}`);
      await submitForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerNumber, book, registryOffice, getMatchRegistryWorkOders]);

  return (
    <>
      <SubmitBox>
        <SubmitButton
          disabled={submitLoading || loadingAssociateData}
          onClick={handleButtonFunction}
        >
          {submitLoading || loadingAssociateData ? (
            <WhiteCircularProgress size={22} />
          ) : (
            Constants.submit
          )}
        </SubmitButton>
      </SubmitBox>
      <AssociateWorkOrderDialog
        workOrders={workOrders}
        registerNumber={registerNumber}
        open={openAssociateDialog}
        handleClose={onCloseDialog}
        isEntrance
        shouldAssociate={shouldAssociate}
        handleAssociate={handleAssociate}
      />
    </>
  );
}
